import React, { useState } from 'react';
import { Database, Loader2, XCircle, ChevronDown, ChevronUp, ClipboardList, Braces, Copy, Check } from 'lucide-react';
import WidgetInfoPopover from './WidgetInfoPopover';

const WIDGET_AGENTS = ['Transcript Analysis Agent (Jamba)'];
const WIDGET_PROMPT = `Extracts structured CRM data from a fraud/scam intake call between an agent and a reporting client.

Returns a JSON object with:
- client_goals: what the client is asking the agent to do (e.g. dispute a charge, freeze account, file a fraud report)
- client_concerns: specific worries raised (e.g. further unauthorized activity, identity theft, account security)
- preferences: any stated resolution or contact preferences
- advisor_action_items: concrete follow-up tasks grounded in the conversation (e.g. initiate chargeback, escalate to fraud team, send replacement card)

Only includes information explicitly discussed during the call.`;

const FieldRow = ({ label, value }) => {
  if (!value) return null;
  return (
    <div>
      <p className="text-xs text-gray-500 font-semibold uppercase tracking-wide mb-1">{label}</p>
      {Array.isArray(value) ? (
        <ul className="space-y-1">
          {value.map((item, i) => (
            <li key={i} className="flex items-start gap-1.5 text-xs text-gray-700">
              <span className="mt-1 w-1.5 h-1.5 rounded-full shrink-0" style={{ background: '#000080' }} />
              {item}
            </li>
          ))}
        </ul>
      ) : (
        <p className="text-xs text-gray-700 bg-gray-50 rounded-lg p-2.5 leading-relaxed">{value}</p>
      )}
    </div>
  );
};

const CRMEntryUpdater = ({ transcript, callStatus, onGenerate, result, isLoading, error }) => {
  const [expanded, setExpanded] = useState(true);
  const [showRaw, setShowRaw] = useState(false);
  const [copied, setCopied] = useState(false);
  const canGenerate = !!transcript?.trim() && !isLoading;
  const hasData = !!result?.data;

  const rawJson = hasData ? JSON.stringify(result.data, null, 2) : '';

  const handleCopy = () => {
    navigator.clipboard.writeText(rawJson).then(() => {
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    });
  };

  return (
    <div className="bg-white rounded-xl shadow-md shadow-blue-900/5 overflow-hidden border border-violet-100">
      {/* Header */}
      <div className="px-4 py-3 border-b border-gray-100 flex items-center justify-between bg-gradient-to-r from-[#000080]/5 to-[#000080]/10">
        <div className="flex items-center gap-2">
          <Database size={15} style={{ color: '#000080' }} />
          <h3 className="font-semibold text-gray-800 text-sm">CRM Entry Updater</h3>
          {isLoading && (
            <span className="w-1.5 h-1.5 rounded-full animate-pulse" style={{ background: '#000080' }} title="Generating..." />
          )}
        </div>
        <div className="flex items-center gap-2">
          <WidgetInfoPopover agents={WIDGET_AGENTS} prompt={WIDGET_PROMPT} />
          {hasData && (
            <>
              <button
                onClick={() => { setShowRaw(v => !v); setExpanded(true); }}
                className="p-1.5 rounded-lg transition-colors text-xs font-medium flex items-center gap-1 hover:bg-white/70"
              style={showRaw ? { backgroundColor: 'rgba(0,0,128,0.1)', color: '#000080' } : { color: '#6b7280' }}
                title={showRaw ? 'Show formatted view' : 'View raw JSON'}
              >
                <Braces size={14} />
              </button>
              <button
                onClick={() => setExpanded(v => !v)}
                className="p-1.5 rounded-lg hover:bg-white/70 text-gray-500 hover:text-gray-700 transition-colors"
                title={expanded ? 'Collapse' : 'Expand'}
              >
                {expanded ? <ChevronUp size={14} /> : <ChevronDown size={14} />}
              </button>
            </>
          )}
        </div>
      </div>

      {/* Body */}
      <div className="p-4 space-y-3">
        {/* Generate button */}
        <button
          onClick={onGenerate}
          disabled={!canGenerate}
          className={`w-full flex items-center justify-center gap-2 py-2.5 px-4 rounded-xl text-sm font-semibold transition-all duration-200 ${canGenerate ? 'text-white shadow-sm hover:shadow-md' : 'bg-gray-100 text-gray-400 cursor-not-allowed'}`}
          style={canGenerate ? { background: '#000080' } : {}}
        >
          {isLoading ? (
            <>
              <Loader2 size={15} className="animate-spin" />
              Generating CRM Entry...
            </>
          ) : (
            <>
              <ClipboardList size={15} />
              Generate CRM Entry
            </>
          )}
        </button>

        {/* Hint when no transcript yet */}
        {!transcript?.trim() && !isLoading && (
          <p className="text-xs text-center text-gray-400">
            Available once the call has started
          </p>
        )}

        {/* Error */}
        {error && !hasData && (
          <div className="flex items-start gap-2 p-3 bg-red-50 rounded-lg text-xs text-red-600">
            <XCircle size={14} className="shrink-0 mt-0.5" />
            <span>{error}</span>
          </div>
        )}

        {/* Result — formatted view */}
        {hasData && expanded && !showRaw && (
          <div className="space-y-3 pt-1">
            <FieldRow label="Client Goals" value={result.data.client_goals} />
            <FieldRow label="Client Concerns" value={result.data.client_concerns} />
            <FieldRow label="Preferences" value={result.data.preferences} />
            <FieldRow label="Advisor Action Items" value={result.data.advisor_action_items} />
          </div>
        )}

        {/* Result — raw JSON view */}
        {hasData && expanded && showRaw && (
          <div className="relative">
            <button
              onClick={handleCopy}
              className="absolute top-2 right-2 p-1.5 rounded-lg bg-gray-700 hover:bg-gray-600 text-gray-300 hover:text-white transition-colors"
              title="Copy JSON"
            >
              {copied ? <Check size={12} className="text-green-400" /> : <Copy size={12} />}
            </button>
            <pre className="text-xs text-gray-300 bg-gray-900 rounded-lg p-3 overflow-x-auto leading-relaxed whitespace-pre-wrap break-words">
              {rawJson}
            </pre>
          </div>
        )}
      </div>
    </div>
  );
};

export default CRMEntryUpdater;
